import React, { type FC, useCallback, useState } from 'react'

import { ApiError, type ScriptCategory } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Error, extendDefaultMatchers } from '@/components/ui/Error.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import {
  type CreateFolderFormValues,
  CreateFolderSchema,
} from '@/dialogs/CreateScriptsFolderDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { addScriptsCategoryFx } from '@/models/scripts/categories.ts'

interface Props extends DialogProps {
  onSuccessfulAdd?: (folder: ScriptCategory) => void
}

const editErrorMatchers = extendDefaultMatchers({
  'This folder name already exists': { status: 409 },
})

export const CreateScriptsFolderDialog: FC<Props> = ({
  isOpen,
  onClose,
  onSuccessfulAdd,
}) => {
  const inviteFormValues = {
    folderName: '',
  }
  const addingCategory = useUnit(addScriptsCategoryFx.pending)

  const editForm = useForm<CreateFolderFormValues>({
    defaultValues: inviteFormValues,
    resolver: zodResolver(CreateFolderSchema),
  })

  const {
    handleSubmit,
    formState: { errors },
  } = editForm

  const [error, setError] = useState<Nullable<ApiError>>(null)

  const onCancelClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault()
    onClose()
  }

  const onSubmit = useCallback(
    async (values: CreateFolderFormValues) => {
      const name = values.folderName

      try {
        const folder = await addScriptsCategoryFx(name)

        onSuccessfulAdd?.(folder)
        onClose()
      } catch (error) {
        setError(error as ApiError)
      }
    },
    [onSuccessfulAdd, onClose],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <Dialog.Header className="items-center">
        <Dialog.Title className="text-3xl">Create a new Folder</Dialog.Title>
        <Dialog.Description className="text-center">
          Organize your scripts by creating folders
        </Dialog.Description>
      </Dialog.Header>
      <FormProvider {...editForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={editForm.control}
            name="folderName"
            render={({ field }) => (
              <div className="mt-6 flex flex-col gap-2">
                <label className="text-sm font-medium text-title">
                  Folder Name
                </label>
                <TextField
                  {...field}
                  autoFocus
                  aria-selected
                  testId="folder-name-input"
                  placeholder="Enter Name"
                />
                {errors.folderName && (
                  <span className="text-danger-default">
                    {errors.folderName.message}
                  </span>
                )}
              </div>
            )}
          />
          <Error
            error={error}
            matchers={editErrorMatchers}
            closable
            className="mt-4"
          />
          <Dialog.ClosableFooter onClose={onCancelClick}>
            <Button
              type="submit"
              isLoading={addingCategory}
              testId="create-folder-button"
            >
              Create
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
